/*!
 * Copyright (c) REWE Digital GmbH
 */
import { Host, h, Build } from "@stencil/core";
import { gotoPage, replaceTranslation } from "@rdss/core";
const DefaultSnippets = {
    labelAriaCartOverview: 'Warenkorbübersicht',
    shippingCostsReached: 'Versandkostenfreie Lieferung',
    totalCartValue: 'Gesamtsumme ink. MwSt.',
    labelAriaCartSummary: 'Warenkorbwert und Differenz von Bestellwert für kostenfreie Lieferung',
    toCart: 'Zum Warenkorb',
    addToCart: 'Zum Warenkorb hinzugefügt',
    labelAriaOrderValue: 'Differenz von Bestellwert für kostenfreie Lieferung',
    labelAriaPresentCartValue: 'Aktueller Warenkorbwert',
};
/** .
 /**.
 * @deprecated
 * This component is deprecated. In the future, please use the "vx-cart-summary"
 * in conjunction with "vx-drawer" to better separate responsibilities.
 *
 * This component represents shopping cart data in an overlay.
 * It reacts to changes in the data and, if necessary, displays the overlay and
 * hides it again after a configurable timeout.
 * The behavior is optimized for display on different devices.
 *
 * @example
 *
 * <vx-cart-overlay
 *     cartData={cartData}
 *     triggerElement={triggerElement}>
 * </vx-cart-overlay>
 *
 * @part container - The whole container.
 * @part wrapper - The wrapper for the cart content.
 */
// eslint-disable-next-line require-jsdoc
export class VxCartOverlay {
    constructor() {
        /**
         * The current count of the line items.
         */
        this.cartItemsCount = 0;
        /**
         * Registers the listeners for the representation behavior.
         */
        this.registerListeners = () => {
            if (Build.isServer)
                return;
            document.ontouchmove = () => this.hide();
            if (this.triggerElement === undefined)
                return;
            this.triggerElement.addEventListener('mouseenter', this.show, { passive: true });
            this.triggerElement.addEventListener('mouseleave', this.hide, { passive: true });
            this.triggerElement.addEventListener('touchstart', this.handleTouchStart, { passive: true });
            if (!this.isTouchDevice) {
                this.triggerElement.addEventListener('click', this.gotoCartPage, { passive: true });
            }
        };
        /**
         * This helper function makes the browser jump to the shopping cart page.
         */
        this.gotoCartPage = () => {
            if (location.pathname.includes(this.cartUrl))
                return;
            gotoPage(this.cartUrl);
        };
        /**
         * Handles the 'touchstart' event. If the event is triggered outside the host component,
         * it sets the document's scroll event to the `hide` method and toggles the display of the cart.
         */
        this.handleTouchStart = (e) => {
            if (e.composedPath().includes(this.host))
                return;
            document.onscroll = () => this.hide();
            this.toggleDisplay();
        };
        /**
         * Shows the overlay.
         */
        this.show = () => setTimeout(() => {
            this.cartIsDisplayed = true;
            this.clearTimeout();
            this.hideWithTimeout();
        });
        /**
         * Hides the overlay.
         */
        this.hide = () => setTimeout(() => {
            this.cartHasNewItem = false;
            this.cartIsDisplayed = false;
        });
        /**
         * Clears the timeout of the timer.
         */
        this.clearTimeout = () => {
            window.clearTimeout(this.timer);
        };
        /**
         * Hides the cart overlay with timeout.
         */
        this.hideWithTimeout = () => {
            this.timer = window.setTimeout(this.hide.bind(this), this.closingTime);
        };
        this.snippets = DefaultSnippets;
        this.triggerElement = undefined;
        this.cartUrl = '/checkout/cart';
        this.closingTime = 5000;
        this.cartData = undefined;
        this.newItemVibration = true;
        this.vibrationDuration = 200;
        this.animation = 'none';
        this.animationDuration = '.3s';
        this.showNoCartItemsMessage = false;
        this.cartIsDisplayed = false;
        this.cartHasNewItem = undefined;
    }
    /**
     * Watches for snippet changes and merges the default values with the
     * new prop values.
     */
    setSnippets(newVal, oldVal) {
        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            this.snippets = Object.assign(Object.assign({}, this.snippets), newVal);
        }
    }
    /**
     *  @inheritDoc
     */
    componentDidLoad() {
        var _a;
        (_a = this.triggerElement) === null || _a === void 0 ? void 0 : _a.append(this.host);
        this.registerListeners();
    }
    /**
     * @inheritDoc
     */
    disconnectedCallback() {
        this.unregisterListeners();
    }
    /**
     * Processes the changes in the shopping cart data to
     * switch the display of the shopping cart.
     */
    handleCartChange(newVal, oldVal) {
        if (newVal.cartItemsCount === (oldVal === null || oldVal === void 0 ? void 0 : oldVal.cartItemsCount))
            return;
        const cartItemsCount = newVal.cartItemsCount;
        /*
         prevent the shopping cart from being displayed
         when loading for the first time
         */
        if (this.cartInitialized) {
            this.show();
            this.cartHasNewItem = cartItemsCount > this.cartItemsCount;
        }
        if ('vibrate' in navigator && this.cartHasNewItem && this.newItemVibration) {
            navigator.vibrate(this.vibrationDuration);
        }
        this.cartItemsCount = cartItemsCount;
        this.cartInitialized = true;
    }
    /**
     * @inheritDoc
     */
    render() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        return Build.isBrowser && (h(Host, { key: 'f127abccd811838e21598ef78210268d75d82ee4', onMouseOver: this.clearTimeout, style: { pointerEvents: this.cartIsDisplayed ? 'auto' : 'none' } }, h("div", { key: '3080300564f0885dc1fa83dba7db38b408fe3838', part: "container", "aria-label": this.snippets.labelAriaCartOverview, "aria-hidden": !this.cartIsDisplayed, class: {
                'cart-overlay-container': true,
                show: this.cartIsDisplayed,
                [this.animation]: true,
                'has-new-item': this.cartHasNewItem,
            } }, h("div", { key: '33c2e6c6208c5ea9dd1d298f53ea9346c9097fa4', part: "wrapper", class: "wrapper" }, this.cartHasNewItem && (h("div", { class: "new-item-notification" }, h("div", { class: "new-item-notification-content notification-color" }, h("vx-icon", { icon: "check_circle", customClass: "new-item-icon reinvert" }), h("span", { class: "new-item-text" }, this.snippets.addToCart)))), h("div", { key: '08727616d52ad6f71e1a1d78b28aa55cace6b9e5', class: "summary", "aria-label": this.snippets.labelAriaCartSummary }, h("span", { key: 'f79dadb0ab938ae626032e004e80da031bbccf64', class: "gross-amount-text" }, this.snippets.totalCartValue), h("span", { key: 'f07622b8b9d7e131b6a345f5f3cec8049ead723a', class: "spacer" }), h("span", { key: 'efe07ca6a297a47af31ee19785f4ee351ca561db', class: "gross-amount-value", "aria-label": this.snippets.labelAriaPresentCartValue }, (_c = (_b = (_a = this.cartData) === null || _a === void 0 ? void 0 : _a.summary) === null || _b === void 0 ? void 0 : _b.total) !== null && _c !== void 0 ? _c : '0')), h("div", { key: '42391868bbacb95c744a1b6c29375af7d3ebd45c', class: {
                'shipping-costs-difference': true,
                blink: !!this.cartIsDisplayed,
            } }, h("span", { key: '1d10331b90edc34c39c6a9089381759131a45237', class: { 'cart-green': (_f = (_e = (_d = this.cartData) === null || _d === void 0 ? void 0 : _d.shipping) === null || _e === void 0 ? void 0 : _e.costs) === null || _f === void 0 ? void 0 : _f.free }, "aria-label": this.snippets.labelAriaOrderValue }, replaceTranslation((_j = (_h = (_g = this.cartData) === null || _g === void 0 ? void 0 : _g.shipping) === null || _h === void 0 ? void 0 : _h.costs) === null || _j === void 0 ? void 0 : _j.text, '%shippingCostDifference', (_m = (_l = (_k = this.cartData) === null || _k === void 0 ? void 0 : _k.shipping) === null || _l === void 0 ? void 0 : _l.costs) === null || _m === void 0 ? void 0 : _m.difference))), !location.pathname.includes(this.cartUrl) && this.cartItemsCount > 0 &&
            h("vx-button", { "aria-label": this.snippets.labelAriaToCart, onClick: this.gotoCartPage, fullwidth: true, label: this.snippets.toCart, raised: true }), this.showNoCartItemsMessage && this.cartItemsCount === 0 &&
            h("p", null, this.snippets.noItemInCart)))));
    }
    /**
     * Unregisters the listeners for the representation behavior.
     */
    unregisterListeners() {
        if (!this.triggerElement)
            return;
        this.triggerElement.removeEventListener('mouseenter', this.show);
        this.triggerElement.removeEventListener('mouseleave', this.hide);
        this.triggerElement.removeEventListener('touchstart', this.handleTouchStart);
        this.triggerElement.removeEventListener('click', this.gotoCartPage);
        document.ontouchmove = null;
        document.onscroll = null;
    }
    /**
     * Toggle the display of the cart.
     */
    toggleDisplay() {
        if (this.cartIsDisplayed)
            this.hide();
        if (!this.cartIsDisplayed)
            this.show();
    }
    /**
     * Checks if the current device is a touch device.
     *
     * @returns {boolean} - `true` if the current device is a mobile device, `false` otherwise.
     */
    get isTouchDevice() {
        return 'ontouchstart' in window;
    }
    static get is() { return "vx-cart-overlay"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-cart-overlay.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-cart-overlay.css"]
        };
    }
    static get properties() {
        return {
            "snippets": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "Snippets",
                    "resolved": "Snippets",
                    "references": {
                        "Snippets": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/vx-cart-overlay/types/index.ts::Snippets"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The snippets for elements labels, description and so on.\nHere you can pass your translations."
                },
                "defaultValue": "DefaultSnippets"
            },
            "triggerElement": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "HTMLElement",
                    "resolved": "HTMLElement",
                    "references": {
                        "HTMLElement": {
                            "location": "global",
                            "id": "global::HTMLElement"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "HTML element to trigger the cart overlay."
                }
            },
            "cartUrl": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "'/checkout/cart'."
                        }],
                    "text": "Path/URL of the cart page."
                },
                "attribute": "cart-url",
                "reflect": false,
                "defaultValue": "'/checkout/cart'"
            },
            "closingTime": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "4000ms."
                        }],
                    "text": "The time it takes to close the overlay."
                },
                "attribute": "closing-time",
                "reflect": false,
                "defaultValue": "5000"
            },
            "cartData": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "CartData",
                    "resolved": "CartData",
                    "references": {
                        "CartData": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/vx-cart-overlay/types/index.ts::CartData"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The cart Data."
                }
            },
            "newItemVibration": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If true, the user will receive physical feedback when\nthe shopping cart has received a new item."
                },
                "attribute": "new-item-vibration",
                "reflect": false,
                "defaultValue": "true"
            },
            "vibrationDuration": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "200"
                        }],
                    "text": "The vibration duration in ms."
                },
                "attribute": "vibration-duration",
                "reflect": false,
                "defaultValue": "200"
            },
            "animation": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'none' | 'fade-in' | 'slide-down'",
                    "resolved": "\"fade-in\" | \"none\" | \"slide-down\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "'none'"
                        }],
                    "text": "The animation type of opening and closing overlay."
                },
                "attribute": "animation",
                "reflect": false,
                "defaultValue": "'none'"
            },
            "animationDuration": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "'.3s'"
                        }],
                    "text": "The animation duration of the overlay."
                },
                "attribute": "animation-duration",
                "reflect": false,
                "defaultValue": "'.3s'"
            },
            "showNoCartItemsMessage": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Shows a message if cart is empty."
                },
                "attribute": "show-no-cart-items-message",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "cartIsDisplayed": {},
            "cartHasNewItem": {}
        };
    }
    static get events() {
        return [{
                "method": "cartOverlayClosed",
                "name": "cartOverlayClosed",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event is fired when the car should be closed."
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "host"; }
    static get watchers() {
        return [{
                "propName": "snippets",
                "methodName": "setSnippets"
            }, {
                "propName": "triggerElement",
                "methodName": "componentDidLoad"
            }, {
                "propName": "cartData",
                "methodName": "handleCartChange"
            }];
    }
}
//# sourceMappingURL=vx-cart-overlay.js.map
